import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { format, parseISO, isAfter, isBefore, startOfDay, endOfDay } from 'date-fns';
import { ref, get, query, orderByChild, startAt, endAt } from 'firebase/database';
import { db } from '../firebase';
import { useConfig } from '../context/ConfigContext';
import { ADMIN_COLORS, PAYMENT_STATUS_PARTIAL, SLOT_STATUS_CLOSED } from '../constants/bookingConst';
import {
  getCourtRate,
  getCourtLabel,
  sortTimeSlots,
  formatTimeSlotRange,
  formatHourLabel,
  parseTimeSlotStartHour,
  spinnerRing,
} from '../utils/bookingUtils';
import { isPackageBooking } from '../utils/packageUtils';
import { getSessionCourtNumbers, getSessionTimeRangeInfo, formatSessionRangeText } from '../utils/openPlayUtil';
import { buildWorkbookBlob, downloadWorkbook } from '../utils/xlsxUtil';
import ExportSummaryChart from './ExportSummaryChart';
import { fetchClosedSlotsInRange } from '../utils/slotCloseReasonUtil';
import ConnectionErrorScreen from './ConnectionErrorScreen';
import { useLoadingTimeout } from '../hooks/useLoadingTimeout';
import { getCurrencySymbol } from '../utils/currencyUtil';
// Downpayments. The two payment columns and the PARTIAL row are only
// reported for a club that has the feature switched on, so every other
// club's workbook keeps exactly the columns it has always had.
import { getBookingPaymentState, isPartialPaymentEnabled } from '../utils/partialPaymentUtil';
import '../css/AdminExport.css';

// Shared jassen light palette (constants/bookingConst.js)
const COLORS = ADMIN_COLORS;

// One status checkbox: the box and its label read as a single click target.
const statusBoxStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.4rem',
  fontSize: '0.8rem',
  fontWeight: 600,
  letterSpacing: '.02em',
  color: COLORS.ink,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

// The three things a club sells, each with its own sheet in the workbook.
// They share almost no columns — a court booking has hours per court, a
// package has one block price, an Open Play join has a per-head fee and a
// roster — so squeezing them into one grid left most of every row empty.
const SHEET_COURT = 'Court Bookings';
const SHEET_PACKAGE = 'Packages & Events';
const SHEET_OPEN_PLAY = 'Open Play';
// Hours the admin closed on the Manage availability grid, with the reason.
// Not a sale, so it has no status filter and no money columns.
const SHEET_CLOSED_SLOTS = 'Closed Slots';

// Statuses that mean the booking earned nothing. Every amount on such a row
// reports 0 — the hours and the roster stay, so the row still says what was
// reserved, but no part of the money breakdown claims revenue that was never
// taken.
//
// Each spelling is one flow's word for the same outcome: 'cancelled' is
// guest-initiated, 'disapproved' is an admin rejecting a court booking,
// 'declined' is an admin rejecting an Open Play join, 'removed' is an admin
// taking an already-confirmed player back off a session, and
// 'awaiting_payment' / 'pending_payment' are checkouts abandoned before a
// receipt was ever attached — a booking only leaves those two by uploading
// one, so anything still sitting there was never paid for.
const VOIDED_STATUSES = [
  'cancelled',
  'disapproved',
  'declined',
  'removed',
  'awaiting_payment',
  'pending_payment',
];

/**
 * Whether a status means no money changed hands.
 *
 * @param {string} status
 * @returns {boolean}
 */
const isVoidedStatus = (status) => VOIDED_STATUSES.includes(String(status || '').toLowerCase());

/**
 * The status filter offered above the preview.
 *
 * Each checkbox maps to the raw status values the app actually stores, which
 * do not match one-to-one — a rejection is 'disapproved' on a court booking
 * but 'declined' on an Open Play join, and an abandoned checkout is
 * 'awaiting_payment' on one and 'pending_payment' on the other. Grouping
 * them here means every record falls into exactly one box, so a status can
 * never quietly drop out of the export.
 *
 * The abandoned checkouts sit under CANCELLED rather than getting a box of
 * their own: a booking leaves that state by attaching a receipt, so one
 * still in it is a reservation that was started and never paid for — the
 * same outcome as a cancellation, and the same 0 in every money column.
 */
const STATUS_FILTERS = [
  { key: 'confirmed', label: 'CONFIRMED', statuses: ['confirmed'] },
  // Confirmed against a downpayment (Config -> Pricing -> "Partial Payment
  // Allowed"). A box of its own rather than a subset of CONFIRMED, so the
  // "exactly one box" rule above still holds and CONFIRMED keeps meaning
  // "settled": a row reported as PARTIAL is one the club is still owed
  // money on. The record's stored status is still 'confirmed' — the two
  // part company on the row, not in Firebase (see the row builder).
  { key: 'partial', label: 'PARTIAL', statuses: [PAYMENT_STATUS_PARTIAL] },
  { key: 'pending', label: 'PENDING', statuses: ['pending'] },
  {
    key: 'cancelled',
    label: 'CANCELLED',
    statuses: ['cancelled', 'disapproved', 'declined', 'removed', 'awaiting_payment', 'pending_payment'],
  },
  // Hours the admin closed on the grid (the Closed Slots sheet). Not a
  // booking status — every closed-slot row is tagged SLOT_STATUS_CLOSED so
  // this box can include or leave out that sheet like any other status.
  { key: SLOT_STATUS_CLOSED, label: 'CLOSED', statuses: [SLOT_STATUS_CLOSED] },
];

/** Every status any checkbox claims, for spotting one that none of them do. */
const KNOWN_STATUSES = STATUS_FILTERS.flatMap(filter => filter.statuses);

/**
 * The filter the screen opens on — today's reservations that are actually
 * on. PARTIAL rides along with CONFIRMED: those bookings are confirmed too,
 * and opening on CONFIRMED alone would quietly leave the money already
 * taken on them out of the day's report.
 */
// CLOSED is on by default too: closed hours carry no money, so including
// them never changes a total, and leaving the box off made the Closed Slots
// sheet look empty unless the admin knew to tick it.
const DEFAULT_STATUS_KEYS = ['confirmed', 'partial', SLOT_STATUS_CLOSED];

// Rows shown per preview page. The preview is a spot-check of the file,
// not a place to read the whole month, so it stays short and pages instead.
const PREVIEW_PAGE_SIZE = 10;

/**
 * The data rows of a sheet: everything but the header and the totals row,
 * both of which the preview renders separately.
 *
 * @param {{rows: object[]}} sheet
 * @returns {object[]}
 */
const sheetBodyRows = (sheet) => (sheet?.rows || []).slice(1).filter(row => row.variant !== 'footer');

/**
 * One workbook cell as preview text. A blank money cell stays blank rather
 * than reading 0.00, exactly as the sheet renders it.
 *
 * Money keeps TWO DECIMALS here even when the amount is whole, unlike
 * formatMoney elsewhere in the app: this table is a preview of the workbook,
 * whose money cells carry the #,##0.00 number format (see xlsxUtil.js), so a
 * row reading "6" on screen and "6.00" in the file would be a lie about what
 * is being exported. The symbol is prepended for the reader only — the .xlsx
 * itself stores a plain number, which is what keeps the cells summable in
 * Excel.
 *
 * @param {{value: *, type?: string}} cell
 * @param {string} currencySymbol - from Config -> System.
 * @returns {string}
 */
const formatPreviewCell = (cell, currencySymbol) => {
  const value = cell?.value;
  if (value === null || value === undefined || value === '') return '';
  if (cell.type === 'money') {
    return `${currencySymbol}${Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
  return String(value);
};

// Roughly the width of one character in the preview table's font, used to
// reuse the workbook's own column widths on screen. The preview and the
// file then have the same proportions, and a column sized for a roster of
// names on the sheet is that wide here too.
const PREVIEW_PX_PER_CHAR = 7.5;
const PREVIEW_CELL_PADDING = 24;

/**
 * A workbook column width (in characters) as a CSS pixel width.
 *
 * @param {number} chars
 * @returns {number}
 */
const previewColumnPx = (chars) => Math.round((Number(chars) || 14) * PREVIEW_PX_PER_CHAR) + PREVIEW_CELL_PADDING;

/**
 * Style for a pagination button.
 *
 * @param {boolean} disabled
 * @returns {object}
 */
const pagerButtonStyle = (disabled) => ({
  padding: '0.4rem 0.9rem',
  borderRadius: '8px',
  border: `1px solid ${COLORS.line}`,
  background: 'transparent',
  color: COLORS.ink,
  fontSize: '0.82rem',
  fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.45 : 1,
});

/** A text cell for the workbook builder. */
const textCell = (value) => ({ value });

/** A number cell rendered with the workbook's two-decimal money format. */
const moneyCell = (value) => ({ value, type: 'money' });

/** A plain whole-number cell — a count, not an amount. */
const numberCell = (value) => ({ value, type: 'number' });

/** `count` empty cells, for padding a footer across to its totals. */
const blanks = (count) => Array.from({ length: count }, () => ({ value: '' }));

/**
 * Upper case, so the status column sorts and filters as one value per state
 * instead of splitting on how it happened to be stored.
 *
 * @param {{status: string}} row
 * @returns {string}
 */
const statusLabel = (row) => String(row?.status || '').toUpperCase();

/**
 * Confirmed bookings get the green band. The status column still spells it
 * out — the fill is there so a reader can see at a glance how much of a long
 * report is settled, without reading down one column.
 *
 * A partially-paid row gets its own amber band instead: it is neither
 * settled (green would overstate it) nor unremarkable, and the whole reason
 * to scan a report for one is that it still owes money.
 *
 * @param {{status: string}} row
 * @returns {'confirmed'|'partial'|'default'}
 */
const rowVariant = (row) => {
  if (row?.status === 'confirmed') return 'confirmed';
  if (row?.status === PAYMENT_STATUS_PARTIAL) return PAYMENT_STATUS_PARTIAL;
  return 'default';
};

/**
 * Sums the values of a { key: amount } breakdown map.
 *
 * @param {Record<string, number>} totals
 * @returns {number}
 */
const sumTotals = (totals) => Object.values(totals || {})
  .reduce((sum, amount) => sum + (Number(amount) || 0), 0);

/**
 * The (court, slot) pairs on a booking, as { [court]: slots[] }.
 *
 * `timeSlots` is stored slot-first ({ "7-8PM": [1, 2] }) because that is how
 * the grid reads it; the export needs it court-first to put one court per
 * column, so it is pivoted here rather than in the render.
 *
 * @param {object} booking
 * @returns {Record<string, string[]>}
 */
const slotsByCourt = (booking) => {
  const byCourt = {};
  Object.entries(booking?.timeSlots || {}).forEach(([slot, courts]) => {
    const list = Array.isArray(courts) ? courts : Object.keys(courts || {});
    list.forEach((court) => {
      const key = String(Number(court));
      if (!byCourt[key]) byCourt[key] = [];
      byCourt[key].push(slot);
    });
  });
  Object.keys(byCourt).forEach((court) => { byCourt[court] = sortTimeSlots(byCourt[court]); });
  return byCourt;
};

/**
 * "6:00 PM - 9:00 PM" across a set of slots.
 *
 * Slots are stored one hour at a time, so a three-hour block is three
 * entries. The package and Open Play sheets want the block, not the hours,
 * so this reads the first slot's start and the last slot's end rather than
 * listing them.
 *
 * @param {string[]} slots
 * @returns {string} empty when there are no slots
 */
const formatSlotSpan = (slots) => {
  const sorted = sortTimeSlots(slots || []);
  if (sorted.length === 0) return '';
  const startHour = parseTimeSlotStartHour(sorted[0]);
  const endHour = (parseTimeSlotStartHour(sorted[sorted.length - 1]) + 1) % 24;
  return `${formatHourLabel(startHour)} - ${formatHourLabel(endHour)}`;
};

/**
 * "Court 1, Court 2" for a list of court numbers.
 *
 * @param {object} config
 * @param {Array<string|number>} courts
 * @returns {string}
 */
const formatCourtList = (config, courts) => (courts || [])
  .map(Number)
  .sort((a, b) => a - b)
  .map(court => getCourtLabel(config, court))
  .join(', ');

/**
 * The rental items on a booking as "Paddle x2, Ball Machine".
 *
 * The court sheet gives each item its own rate column; the package sheet
 * has one Equipment Total instead, so the items have to be named somewhere
 * or the amount is unattributable.
 *
 * @param {object} booking
 * @returns {string}
 */
const formatEquipmentList = (booking) => (booking?.equipmentRental?.items || [])
  .map((item) => {
    const label = item.label || item.name || item.id || 'Item';
    const quantity = Number(item.quantity) || 0;
    return quantity > 1 ? `${label} x${quantity}` : String(label);
  })
  .join(', ');

/**
 * Reads a Firebase node over a 'yyyy-MM-dd' date range into an array.
 *
 * Dates are stored as strings, so a lexicographic startAt/endAt is a correct
 * and inclusive range. Requires .indexOn: ["date"] on the node, or Firebase
 * streams the whole thing and filters client-side.
 *
 * @param {string} path
 * @param {string} from
 * @param {string} to
 * @returns {Promise<object[]>} records, each with its Firebase key as `id`
 */
const fetchByDateRange = async (path, from, to) => {
  const snapshot = await get(query(ref(db, path), orderByChild('date'), startAt(from), endAt(to)));
  if (!snapshot.exists()) return [];
  const records = [];
  snapshot.forEach((child) => { records.push({ id: child.key, ...child.val() }); });
  return records;
};

function AdminExport() {
  const { config } = useConfig();
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [loading, setLoading] = useState(false);
  // Watchdog over the fetch below — a Firebase get() on a dead connection
  // never rejects, so the try/catch in fetchBookings never runs and the
  // spinner would sit there forever (see hooks/useLoadingTimeout.js).
  const { timedOut, restart: restartTimeout } = useLoadingTimeout(loading);
  const [fetched, setFetched] = useState(false);
  // One list per sheet. They're kept apart from the moment they're built
  // rather than tagged and split at export time, because each one carries
  // different fields — a package row has no per-court hours and an Open Play
  // row has a roster instead of courts.
  const [courtRows, setCourtRows] = useState([]);
  const [packageRows, setPackageRows] = useState([]);
  const [openPlayRows, setOpenPlayRows] = useState([]);
  const [closedSlotRows, setClosedSlotRows] = useState([]);

  // Which status checkboxes are ticked. The fetch always pulls the whole
  // range, so toggling one of these re-filters what is already in memory
  // rather than going back to Firebase — the join records carry base64
  // payment receipts, and re-reading them on every click would be costly.
  const [statusKeys, setStatusKeys] = useState(DEFAULT_STATUS_KEYS);

  const activeStatuses = useMemo(() => new Set(
    STATUS_FILTERS.filter(filter => statusKeys.includes(filter.key)).flatMap(filter => filter.statuses),
  ), [statusKeys]);

  const allSelected = statusKeys.length === STATUS_FILTERS.length;

  /**
   * Whether a row passes the current status filter.
   *
   * A status no checkbox claims (an older record, or one added later) is
   * only shown under ALL — it has nowhere else to appear, and dropping it
   * from every view would hide it from the export without saying so.
   *
   * @param {{status: string}} row
   * @returns {boolean}
   */
  const matchesFilter = useCallback((row) => {
    const status = String(row?.status || '').toLowerCase();
    if (!KNOWN_STATUSES.includes(status)) return allSelected;
    return activeStatuses.has(status);
  }, [activeStatuses, allSelected]);

  // What the checkboxes let through, per sheet. The summary cards, the
  // preview table and the workbook all read these, so the file always
  // matches what is on screen.
  const filteredCourtRows = useMemo(() => courtRows.filter(matchesFilter), [courtRows, matchesFilter]);
  const filteredPackageRows = useMemo(() => packageRows.filter(matchesFilter), [packageRows, matchesFilter]);
  const filteredOpenPlayRows = useMemo(() => openPlayRows.filter(matchesFilter), [openPlayRows, matchesFilter]);
  const filteredClosedSlotRows = useMemo(() => closedSlotRows.filter(matchesFilter), [closedSlotRows, matchesFilter]);

  const previewData = useMemo(
    () => [...filteredCourtRows, ...filteredPackageRows, ...filteredOpenPlayRows, ...filteredClosedSlotRows],
    [filteredCourtRows, filteredPackageRows, filteredOpenPlayRows, filteredClosedSlotRows],
  );

  // Only used to say "showing 59 of 142" — the fetched range before filtering.
  const fetchedCount = courtRows.length + packageRows.length + openPlayRows.length + closedSlotRows.length;

  /**
   * Ticks or unticks one status box. ALL is a master toggle rather than a
   * box of its own: it selects every status, and unticking it clears them.
   *
   * @param {string} key one of STATUS_FILTERS[].key
   * @returns {void}
   */
  const toggleStatus = (key) => {
    setStatusKeys(current => (
      current.includes(key) ? current.filter(item => item !== key) : [...current, key]
    ));
  };

  /**
   * @param {boolean} checked
   * @returns {void}
   */
  const toggleAll = (checked) => {
    setStatusKeys(checked ? STATUS_FILTERS.map(filter => filter.key) : []);
  };

  // One column per court and per rental item, derived from config so adding
  // either gives it a column with no code change. Keyed by court number /
  // equipment id — both survive a rename, which the label used as the header
  // does not.
  const courtColumns = useMemo(() => (
    (config?.courts || [])
      .map(c => ({ key: String(Number(c.number)), label: getCourtLabel(config, c.number) }))
      .sort((a, b) => Number(a.key) - Number(b.key))
  ), [config]);

  const equipmentColumns = useMemo(() => (
    (config?.equipment || []).map(e => ({ key: String(e.id), label: e.label || e.name || String(e.id) }))
  ), [config]);

  // "Amount Paid" and "Balance Due" are only worth two columns to a club
  // that takes downpayments — everywhere else the first would just repeat
  // the grand total and the second would be a column of zeroes. APPENDED
  // at the end of each sheet, so turning the feature on never shifts a
  // column someone's own spreadsheet formulas point at.
  const showPaymentColumns = isPartialPaymentEnabled(config);

  const fetchBookings = useCallback(async () => {
    if (!db) {
      alert('Database not configured');
      return;
    }

    setLoading(true);
    try {
      // Range-queried on the server rather than downloaded whole. This used
      // to be get(ref(db, 'bookings')) — every booking ever made, including
      // every payment receipt stored on those records — filtered down to the
      // export range afterwards in JS. Dates are stored as 'yyyy-MM-dd', so a
      // lexicographic startAt/endAt is a correct (and inclusive) date range.
      // Requires .indexOn: ["date"] on /bookings, or Firebase falls back to
      // streaming the whole node and filtering client-side.
      // Three range-queried reads rather than one: Open Play lives in its own
      // openPlayJoins collection (no court is claimed, so it was never a
      // booking record), and its sessions carry the court/time details a join
      // only points at by id.
      const [allBookings, allJoins, allSessions, closedSlots] = await Promise.all([
        fetchByDateRange('bookings', startDate, endDate),
        fetchByDateRange('openPlayJoins', startDate, endDate),
        fetchByDateRange('openPlaySessions', startDate, endDate),
        // Never fails the booking export: a refused or dropped read here
        // just means an empty Closed Slots sheet, not no report at all.
        fetchClosedSlotsInRange(startDate, endDate).catch((error) => {
          console.error('Closed slots could not be loaded for the export:', error);
          return [];
        }),
      ]);

      // The queries above already bound the range; this stays as a guard for
      // the un-indexed fallback, where Firebase returns the whole node.
      const start = startOfDay(parseISO(startDate));
      const end = endOfDay(parseISO(endDate));
      const inRange = (record) => {
        if (!record?.date) return false;
        const recordDate = parseISO(record.date);
        return (isAfter(recordDate, start) || recordDate.getTime() === start.getTime()) &&
               (isBefore(recordDate, end) || recordDate.getTime() === end.getTime());
      };

      const filteredBookings = allBookings.filter(inRange);
      const sessionsById = {};
      allSessions.filter(inRange).forEach((session) => { sessionsById[session.id] = session; });

      // Generate preview data with calculated fields
      const adminEmail = config?.adminEmail?.toLowerCase() || '';
      const bookingRows = filteredBookings.map(booking => {
        // A package is sold as one priced block, not as court-hours: breaking
        // it across the per-court columns would invent per-hour figures the
        // guest was never charged. Those go to their own sheet.
        const isPackage = isPackageBooking(booking);
        const isAdmin = booking.email?.toLowerCase() === adminEmail;
        const isConfirmed = booking.status === 'confirmed';

        // Confirmed but not paid off. The stored status stays 'confirmed'
        // — the court is genuinely reserved — so this is read off the
        // payment fields, and is false for every booking made before
        // downpayments existed.
        const payment = getBookingPaymentState(booking);
        const isPartiallyPaid = isConfirmed && payment.isPartial;

        // Revenue actually taken, used only by the on-screen summary cards.
        // Deliberately NOT the Grand Total column any more — see below.
        // A partial booking counts what was RECEIVED, not what it will be
        // worth once settled: the card is headed "collected".
        const collected = isPartiallyPaid ? payment.amountPaid : (booking.paymentAmount || 0);
        const totalPaid = isConfirmed && !isAdmin ? collected : 0;

        // A multi-date booking is stored as one record PER DATE, and the
        // whole-reservation costs — the processing fee, the equipment rental and
        // a package's block price — are written onto every one of those
        // records even though they are charged once (see App.jsx's
        // createPendingBooking, where only the primary record's paymentAmount
        // includes them). Reporting them on every row would triple a 3-date
        // booking's fee and its paddle rental in the column totals, so the
        // secondary rows carry their court hours only. A single-date booking
        // has no groupId and is unaffected.
        const isGroupSecondary = !!booking.groupId && booking.groupPrimary !== true;

        // Taken straight off the booking record — this is the fee that was
        // actually charged at the time, so editing Config -> Pricing can no
        // longer retroactively rewrite what past exports report.
        // A cancelled or rejected booking earned nothing, so every amount on
        // its row is 0 — including the per-court and per-item breakdown, or
        // the columns would add up to a grand total they no longer match.
        const isVoided = isVoidedStatus(booking.status);
        const bookingFee = isGroupSecondary || isVoided ? 0 : (Number(booking.bookingFee) || 0);
        // What the club actually collected in fees, for the summary card
        // above the table. The column reports the fee the booking carries
        // whatever became of it; this one only counts money taken.
        const feeCollected = isConfirmed && !isAdmin ? bookingFee : 0;

        // ---- Per-court breakdown -------------------------------------
        // One entry per court the booking actually used: the hours booked
        // on it, and what those hours cost at the rate that applied on that
        // date (getCourtRate is date-aware, so a weekend band doesn't get
        // priced at the weekday one).
        const courtSlots = isPackage ? {} : slotsByCourt(booking);
        const courtTotals = {};
        if (!isVoided) {
          Object.entries(courtSlots).forEach(([court, slots]) => {
            courtTotals[court] = slots.reduce(
              (sum, slot) => sum + (Number(getCourtRate(config, court, slot, booking.sportId, booking.date)) || 0),
              0,
            );
          });
        }

        // ---- Per-equipment breakdown ---------------------------------
        // Keyed by the config id so a renamed item still lines up with its
        // column; `subtotal` is what was charged, not a recomputed price.
        const equipmentTotals = {};
        if (!isPackage && !isGroupSecondary && !isVoided) {
          (booking.equipmentRental?.items || []).forEach((item) => {
            const key = String(item.id ?? item.label ?? '');
            if (!key) return;
            equipmentTotals[key] = (equipmentTotals[key] || 0) + (Number(item.subtotal) || 0);
          });
        }

        // ---- Grand total ---------------------------------------------
        // The sum of this row's own breakdown, not what was paid. The old
        // export wrote paymentAmount here, which read as 0 on every pending
        // or cancelled booking and disagreed with the court columns beside
        // it on every multi-date one — the breakdown said 1,050 and the
        // grand total said 610.
        //
        // A package sells no per-hour court time, so its total is the block
        // price plus whatever rental rode along on it, read off the record
        // rather than rebuilt from per-court figures that don't exist.
        //
        // The processing fee is reported in its own column and left out:
        // "grand total" here means courts + equipment, matching the columns
        // it sits at the end of.
        const packagePrice = isGroupSecondary || isVoided ? 0 : (Number(booking.packagePrice) || 0);
        const packageEquipmentTotal = isGroupSecondary || isVoided
          ? 0
          : (Number(booking.equipmentRental?.total) || 0);
        const grandTotal = isPackage
          ? packagePrice + packageEquipmentTotal
          : sumTotals(courtTotals) + sumTotals(equipmentTotals);

        // Fields shared by both sheets, so the summary cards and the preview
        // table can read any row without knowing which one it belongs to.
        const shared = {
          id: booking.id,
          sheet: isPackage ? SHEET_PACKAGE : SHEET_COURT,
          confirmationId: booking.confirmationId || 'N/A',
          date: booking.date || '',
          userName: booking.userName || '',
          email: booking.email || '',
          phone: booking.phone || '',
          // 'partial' is a REPORTING status, not a stored one — see
          // STATUS_FILTERS. Everything above this line reads the record's
          // real status, so nothing about how the row is priced changes.
          status: isPartiallyPaid ? PAYMENT_STATUS_PARTIAL : (booking.status || 'pending'),
          totalPaid,
          grandTotal,
          bookingFee,
          feeCollected,
          // The two payment columns. Zeroed on a secondary date-record of a
          // multi-date reservation for the same reason its fee is: the
          // payment fields are mirrored across the group, and reporting
          // them on every row would multiply one downpayment by its dates.
          amountPaid: isGroupSecondary || isVoided
            ? 0
            : (isPartiallyPaid ? payment.amountPaid : (isConfirmed ? Number(booking.paymentAmount) || 0 : 0)),
          balanceDue: isGroupSecondary || isVoided || !isPartiallyPaid ? 0 : payment.balanceDue,
        };

        if (isPackage) {
          // The hours actually sold, not the cleanup slots also written into
          // `timeSlots` — packageEventSlots is what the guest was quoted.
          const eventSlots = booking.packageEventSlots || Object.keys(booking.timeSlots || {});
          return {
            ...shared,
            packageName: booking.packageName || 'Package',
            courtsLabel: formatCourtList(config, Object.keys(slotsByCourt(booking))),
            timeLabel: formatSlotSpan(eventSlots),
            hours: Number(booking.packageHours) || sortTimeSlots(eventSlots).length,
            packagePrice,
            equipmentLabel: isGroupSecondary ? '' : formatEquipmentList(booking),
            equipmentTotal: packageEquipmentTotal,
          };
        }

        return { ...shared, courtSlots, courtTotals, equipmentTotals };
      });

      // ---- Open Play joins -------------------------------------------
      // One row per join, which is one booking: a guest paying for a group
      // books once and names everyone, so the roster is a cell rather than
      // extra rows. `pending_payment` joins are kept for the same reason
      // `awaiting_payment` court bookings are — an abandoned checkout is
      // still something an admin may want to see in the range.
      const openPlay = allJoins.filter(inRange).map((join) => {
        const session = join.sessionId ? sessionsById[join.sessionId] : null;
        const isConfirmed = join.status === 'confirmed';
        // What the club was PAID for, which is not always who is still on
        // the roster: taking one person off a group of four (see
        // removeOpenPlayPlayers) drops numPlayers to 3 and stamps
        // paidPlayers: 4. Billing from the live head count would rewrite
        // revenue already banked every time an admin edits a roster.
        // Absent on every join that has never had anyone removed, which is
        // almost all of them — those bill from numPlayers exactly as before.
        const players = Number(join.paidPlayers ?? join.numPlayers) || 0;
        // A declined join earned nothing. The head count stays — it says how
        // many were on the request — but the per-head price goes to 0 so the
        // row still multiplies out to its own grand total.
        const pricePerPlayer = isVoidedStatus(join.status) ? 0 : (Number(join.pricePerPlayer) || 0);
        // Recomputed from the breakdown rather than trusting the stored
        // totalAmount, for the same reason the court sheet no longer reports
        // paymentAmount: the row has to add up to the columns beside it.
        const grandTotal = pricePerPlayer * players;

        return {
          id: join.id,
          sheet: SHEET_OPEN_PLAY,
          confirmationId: join.confirmationId || 'N/A',
          date: join.date || '',
          sportName: join.sportName || '',
          sessionLabel: session
            ? formatCourtList(config, getSessionCourtNumbers(session))
            : '',
          // Via getSessionTimeRangeInfo, NOT formatSlotSpan: that takes the
          // earliest and latest slot of a flat list, which for an overnight
          // session is 12AM and 11PM — it would print "12:00 MN - 12:00 MN"
          // for a session that actually runs 10PM to 6AM. The session helper
          // puts the after-midnight hours on a timeline anchored to the start
          // date, where 6AM is genuinely after 10PM.
          timeLabel: session
            ? (() => {
              const r = getSessionTimeRangeInfo(session);
              return r.startHour === null ? '' : formatSessionRangeText(r.startHour, r.endHour, r.endsNextDay);
            })()
            : (join.timeSlot ? formatTimeSlotRange(join.timeSlot) : ''),
          userName: join.fullName || '',
          email: join.email || '',
          phone: join.phone || '',
          // Named individually on the guest form; an admin walk-in is
          // entered under one name, so it falls back to the booker's.
          playerNames: (Array.isArray(join.players) && join.players.length > 0
            ? join.players.map(p => p?.name).filter(Boolean)
            : [join.fullName]).filter(Boolean).join(', '),
          players,
          pricePerPlayer,
          source: join.addedByAdmin === true ? 'Admin' : 'Guest',
          status: join.status || 'pending',
          totalPaid: isConfirmed ? grandTotal : 0,
          grandTotal,
          // Open Play is priced per head with no reservation fee attached.
          bookingFee: 0,
          feeCollected: 0,
        };
      });

      setCourtRows(bookingRows.filter(row => row.sheet === SHEET_COURT));
      setPackageRows(bookingRows.filter(row => row.sheet === SHEET_PACKAGE));
      setOpenPlayRows(openPlay);
      // Tagged so the CLOSED status box filters them like any other row.
      setClosedSlotRows(closedSlots.map(slot => ({ ...slot, status: SLOT_STATUS_CLOSED })));
      setFetched(true);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      alert('Error fetching bookings: ' + error.message);
    }
    setLoading(false);
  }, [startDate, endDate, config]);

  // Load today's bookings as soon as the screen opens, so it lands on
  // something useful instead of an empty panel. Deliberately once only —
  // changing the dates afterwards is the admin's cue to press Fetch, and
  // re-running on every keystroke in a date field would hammer Firebase.
  // Held until `config` arrives, since the court rates come from it.
  const didInitialFetch = useRef(false);
  useEffect(() => {
    if (didInitialFetch.current || !db || !config) return;
    didInitialFetch.current = true;
    fetchBookings();
  }, [config, fetchBookings]);

  /**
   * Try Again on the timeout screen — restarts the countdown and re-runs the
   * same date-range query.
   *
   * @returns {void}
   */
  const handleRetry = () => {
    restartTimeout();
    fetchBookings();
  };

  /**
   * The Court Bookings sheet: one row per booking record, hours booked per
   * court, then the same courts again as rates, then the rental items.
   *
   * @returns {object} an XlsxSheet
   */
  const buildCourtSheet = useCallback(() => {
    const headers = [
      'Booking ID',
      'Date',
      ...courtColumns.map(c => c.label),
      'User Name',
      'Email',
      'Phone',
      'Status',
      // Breakdown of what makes up the grand total. Courts appear twice —
      // hours booked above, what those hours cost here — but equipment gets
      // a rate column only: a YES/NO usage column said nothing the rate
      // column beside it didn't already say by being filled in.
      ...courtColumns.map(c => `${c.label} Total`),
      ...equipmentColumns.map(e => `${e.label} Total`),
      'Grand Total',
      'Processing Fee',
      ...(showPaymentColumns ? ['Amount Paid', 'Balance Due'] : []),
    ];

    const rows = [{ variant: 'header', cells: headers.map(textCell) }];
    let totalGrand = 0;
    let totalBookingFee = 0;
    let totalAmountPaid = 0;
    let totalBalanceDue = 0;
    // Column-wise sums for the footer, so each court/item can be reconciled
    // on its own rather than only against the grand total.
    const courtColumnTotals = {};
    const equipmentColumnTotals = {};

    filteredCourtRows.forEach((row) => {
      totalGrand += row.grandTotal;
      totalBookingFee += row.bookingFee;
      totalAmountPaid += row.amountPaid || 0;
      totalBalanceDue += row.balanceDue || 0;
      courtColumns.forEach((court) => {
        courtColumnTotals[court.key] = (courtColumnTotals[court.key] || 0) + (row.courtTotals[court.key] || 0);
      });
      equipmentColumns.forEach((item) => {
        equipmentColumnTotals[item.key] = (equipmentColumnTotals[item.key] || 0) + (row.equipmentTotals[item.key] || 0);
      });

      rows.push({
        variant: rowVariant(row),
        cells: [
          textCell(row.confirmationId),
          textCell(row.date),
          ...courtColumns.map(court => textCell(
            row.courtSlots[court.key]?.length
              ? row.courtSlots[court.key].map(formatTimeSlotRange).join(', ')
              : ''
          )),
          textCell(row.userName),
          textCell(row.email),
          textCell(row.phone),
          textCell(statusLabel(row)),
          ...courtColumns.map(court => moneyCell(row.courtTotals[court.key] || null)),
          ...equipmentColumns.map(item => moneyCell(row.equipmentTotals[item.key] || null)),
          moneyCell(row.grandTotal),
          moneyCell(row.bookingFee),
          // Blank rather than 0.00 on a row with nothing outstanding — a
          // column of zeroes is harder to scan than a column with three
          // figures in it.
          ...(showPaymentColumns
            ? [moneyCell(row.amountPaid || null), moneyCell(row.balanceDue || null)]
            : []),
        ],
      });
    });

    if (filteredCourtRows.length > 0) {
      rows.push({
        variant: 'footer',
        cells: [
          textCell('TOTAL'),
          ...blanks(1 + courtColumns.length + 4),
          ...courtColumns.map(court => moneyCell(courtColumnTotals[court.key] || 0)),
          ...equipmentColumns.map(item => moneyCell(equipmentColumnTotals[item.key] || 0)),
          moneyCell(totalGrand),
          moneyCell(totalBookingFee),
          ...(showPaymentColumns ? [moneyCell(totalAmountPaid), moneyCell(totalBalanceDue)] : []),
        ],
      });
    }

    // Widths in character units, matched to what each column actually holds:
    // the slot columns carry a list like "7:00 PM - 8:00 PM, 8:00 PM - 9:00 PM",
    // the rate columns carry a formatted amount.
    const columnWidths = [
      30, 12,
      ...courtColumns.map(() => 30),
      24, 28, 16, 18,
      ...courtColumns.map(() => 14),
      // Sized to the header rather than the amount: "Training Balls (set)
      // Total" is far wider than the figure underneath it, and a truncated
      // header makes two rental columns indistinguishable.
      ...equipmentColumns.map(item => Math.max(14, Math.min(28, item.label.length + 8))),
      14, 15,
      ...(showPaymentColumns ? [14, 14] : []),
    ];

    return { name: SHEET_COURT, rows, columnWidths };
  }, [filteredCourtRows, courtColumns, equipmentColumns, showPaymentColumns]);

  /**
   * The Packages & Events sheet: one row per package booking, with the two
   * amounts that make up its total spelled out beside it.
   *
   * @returns {object} an XlsxSheet
   */
  const buildPackageSheet = useCallback(() => {
    const headers = [
      'Booking ID',
      'Date',
      'Package / Event',
      'Courts',
      'Time',
      'Hours',
      'User Name',
      'Email',
      'Phone',
      'Status',
      'Equipment Rented',
      // The breakdown: a package is a block price, plus any rental, and the
      // processing fee is reported separately as it is on the court sheet.
      'Package Price',
      'Equipment Total',
      'Grand Total',
      'Processing Fee',
      ...(showPaymentColumns ? ['Amount Paid', 'Balance Due'] : []),
    ];

    const rows = [{ variant: 'header', cells: headers.map(textCell) }];
    let totalPackage = 0;
    let totalEquipment = 0;
    let totalGrand = 0;
    let totalBookingFee = 0;
    let totalAmountPaid = 0;
    let totalBalanceDue = 0;

    filteredPackageRows.forEach((row) => {
      totalPackage += row.packagePrice;
      totalEquipment += row.equipmentTotal;
      totalGrand += row.grandTotal;
      totalBookingFee += row.bookingFee;
      totalAmountPaid += row.amountPaid || 0;
      totalBalanceDue += row.balanceDue || 0;

      rows.push({
        variant: rowVariant(row),
        cells: [
          textCell(row.confirmationId),
          textCell(row.date),
          textCell(row.packageName),
          textCell(row.courtsLabel),
          textCell(row.timeLabel),
          textCell(row.hours || ''),
          textCell(row.userName),
          textCell(row.email),
          textCell(row.phone),
          textCell(statusLabel(row)),
          textCell(row.equipmentLabel),
          moneyCell(row.packagePrice || null),
          moneyCell(row.equipmentTotal || null),
          moneyCell(row.grandTotal),
          moneyCell(row.bookingFee),
          ...(showPaymentColumns
            ? [moneyCell(row.amountPaid || null), moneyCell(row.balanceDue || null)]
            : []),
        ],
      });
    });

    if (filteredPackageRows.length > 0) {
      rows.push({
        variant: 'footer',
        cells: [
          textCell('TOTAL'),
          ...blanks(10),
          moneyCell(totalPackage),
          moneyCell(totalEquipment),
          moneyCell(totalGrand),
          moneyCell(totalBookingFee),
          ...(showPaymentColumns ? [moneyCell(totalAmountPaid), moneyCell(totalBalanceDue)] : []),
        ],
      });
    }

    return {
      name: SHEET_PACKAGE,
      rows,
      columnWidths: [
        30, 12, 26, 22, 22, 8, 24, 28, 16, 18, 26, 15, 16, 14, 15,
        ...(showPaymentColumns ? [14, 14] : []),
      ],
    };
  }, [filteredPackageRows, showPaymentColumns]);

  /**
   * The Open Play sheet: one row per join, since a join is one booking — a
   * guest who brings three friends pays once and names them all, so the
   * roster is a cell and the head count is the breakdown.
   *
   * @returns {object} an XlsxSheet
   */
  const buildOpenPlaySheet = useCallback(() => {
    const headers = [
      'Booking ID',
      'Date',
      'Sport',
      'Courts',
      'Time',
      'Booked By',
      'Email',
      'Phone',
      'Players',
      'Booked Via',
      'Status',
      // The breakdown: Open Play is priced per head, so the total is the fee
      // times the number of players and nothing else.
      'No. of Players',
      'Price / Player',
      'Grand Total',
    ];

    const rows = [{ variant: 'header', cells: headers.map(textCell) }];
    let totalPlayers = 0;
    let totalGrand = 0;

    filteredOpenPlayRows.forEach((row) => {
      totalPlayers += row.players;
      totalGrand += row.grandTotal;

      rows.push({
        variant: rowVariant(row),
        cells: [
          textCell(row.confirmationId),
          textCell(row.date),
          textCell(row.sportName),
          textCell(row.sessionLabel),
          textCell(row.timeLabel),
          textCell(row.userName),
          textCell(row.email),
          textCell(row.phone),
          textCell(row.playerNames),
          textCell(row.source),
          textCell(statusLabel(row)),
          numberCell(row.players),
          moneyCell(row.pricePerPlayer || null),
          moneyCell(row.grandTotal),
        ],
      });
    });

    if (filteredOpenPlayRows.length > 0) {
      rows.push({
        variant: 'footer',
        cells: [
          textCell('TOTAL'),
          ...blanks(10),
          numberCell(totalPlayers),
          textCell(''),
          moneyCell(totalGrand),
        ],
      });
    }

    return {
      name: SHEET_OPEN_PLAY,
      rows,
      columnWidths: [30, 12, 16, 22, 22, 22, 28, 16, 44, 14, 18, 14, 14, 14],
    };
  }, [filteredOpenPlayRows]);

  /**
   * The Closed Slots sheet: one row per closed hour, ordered by date, court
   * and time, with the reason the admin gave when closing it.
   *
   * @returns {object} an XlsxSheet
   */
  const buildClosedSlotsSheet = useCallback(() => {
    const headers = ['Date', 'Court', 'Time', 'Reason', 'Closed By', 'Closed At'];
    const sorted = [...filteredClosedSlotRows].sort((a, b) => (
      a.date.localeCompare(b.date)
      || Number(a.court) - Number(b.court)
      || parseTimeSlotStartHour(a.timeSlot) - parseTimeSlotStartHour(b.timeSlot)
    ));
    const rows = [{ variant: 'header', cells: headers.map(textCell) }];
    sorted.forEach((row) => {
      rows.push({
        variant: 'default',
        cells: [
          textCell(row.date),
          textCell(getCourtLabel(config, row.court)),
          textCell(formatTimeSlotRange(row.timeSlot)),
          // Hours closed before reasons were captured have none on record.
          textCell(row.reason || '—'),
          textCell(row.closedBy),
          textCell(row.closedAt ? format(new Date(row.closedAt), 'yyyy-MM-dd h:mm a') : ''),
        ],
      });
    });
    return {
      name: SHEET_CLOSED_SLOTS,
      rows,
      columnWidths: [12, 18, 20, 48, 28, 20],
    };
  }, [filteredClosedSlotRows, config]);

  // The workbook itself, built once and used for both the on-screen preview
  // and the download. The preview renders these very rows, so what an admin
  // checks on screen is literally the file they get — there is no second
  // column list to drift out of sync with the exported one.
  //
  // All three tabs are always present, even when a range has no packages or
  // no Open Play: an empty sheet says "none in this range", a missing one
  // reads as "this export doesn't cover that".
  const sheets = useMemo(
    () => [buildCourtSheet(), buildPackageSheet(), buildOpenPlaySheet(), buildClosedSlotsSheet()],
    [buildCourtSheet, buildPackageSheet, buildOpenPlaySheet, buildClosedSlotsSheet],
  );

  // Which sheet's tab is open, and where in it. Both reset when the filter
  // or the fetched range changes — page 4 of the old result set means
  // nothing once the rows underneath it have been replaced.
  const [activeSheet, setActiveSheet] = useState(0);
  const [page, setPage] = useState(0);

  useEffect(() => { setPage(0); }, [activeSheet, statusKeys, courtRows, packageRows, openPlayRows, closedSlotRows]);

  const currentSheet = sheets[activeSheet] || sheets[0];
  const activeSheetName = currentSheet?.name || '';
  const headerCells = currentSheet?.rows?.[0]?.cells || [];
  const bodyRows = sheetBodyRows(currentSheet);
  const footerRow = (currentSheet?.rows || []).find(row => row.variant === 'footer');

  const pageCount = Math.max(1, Math.ceil(bodyRows.length / PREVIEW_PAGE_SIZE));
  // Clamped rather than trusted: a page can fall off the end between renders
  // (unticking a status shrinks the list under the reader's feet).
  const safePage = Math.min(Math.max(page, 0), pageCount - 1);
  const pageStart = safePage * PREVIEW_PAGE_SIZE;
  const pageRows = bodyRows.slice(pageStart, pageStart + PREVIEW_PAGE_SIZE);

  // The table is laid out at the workbook's own column widths and allowed to
  // overrun the card — the panel scrolls sideways. Letting it shrink to fit
  // instead squeezed the wide columns (an Open Play roster of seven names)
  // into a sliver one word across, which turned every row into a paragraph.
  const previewColumnPxWidths = useMemo(
    () => (currentSheet?.columnWidths || []).map(previewColumnPx),
    [currentSheet],
  );
  const previewTableWidth = previewColumnPxWidths.reduce((sum, width) => sum + width, 0);

  const generateWorkbook = () => {
    if (previewData.length === 0) {
      alert('No data to export. Please select a date range and fetch bookings first.');
      return;
    }
    downloadWorkbook(
      buildWorkbookBlob({ sheets }),
      `bookings_report_${startDate}_to_${endDate}.xlsx`,
    );
  };

  // What the donut divides. Every figure is summed from the same filtered
  // rows the workbook is built from, so the chart, the preview and the file
  // always agree — including the equipment rental, which the stat cards this
  // replaced folded silently into one Total Paid number.
  //
  // The processing fee is its own component rather than being folded into a
  // sheet's Grand Total, matching the export, where it sits in a column of
  // its own beside the total rather than inside it.
  const chartGroups = useMemo(() => {
    const sumBy = (rows, pick) => rows.reduce((total, row) => total + (Number(pick(row)) || 0), 0);

    return [
      {
        key: 'court',
        label: SHEET_COURT,
        parts: [
          { key: 'courts', label: 'Court hours', value: sumBy(filteredCourtRows, row => sumTotals(row.courtTotals)) },
          { key: 'equipment', label: 'Equipment rental', value: sumBy(filteredCourtRows, row => sumTotals(row.equipmentTotals)) },
          { key: 'fee', label: 'Processing fee', value: sumBy(filteredCourtRows, row => row.bookingFee) },
        ],
      },
      {
        key: 'package',
        label: SHEET_PACKAGE,
        parts: [
          { key: 'price', label: 'Package price', value: sumBy(filteredPackageRows, row => row.packagePrice) },
          { key: 'equipment', label: 'Equipment rental', value: sumBy(filteredPackageRows, row => row.equipmentTotal) },
          { key: 'fee', label: 'Processing fee', value: sumBy(filteredPackageRows, row => row.bookingFee) },
        ],
      },
      {
        key: 'openPlay',
        label: SHEET_OPEN_PLAY,
        parts: [
          { key: 'players', label: 'Player fees', value: sumBy(filteredOpenPlayRows, row => row.grandTotal) },
        ],
      },
    ];
  }, [filteredCourtRows, filteredPackageRows, filteredOpenPlayRows]);

  // Counts per status, shown as chips beside the chart. They are records,
  // not pesos, so they are deliberately not slices of it.
  const chartStatusCounts = useMemo(() => {
    const tones = { confirmed: 'good', partial: 'warning', pending: 'warning', cancelled: 'critical', [SLOT_STATUS_CLOSED]: 'critical' };
    return STATUS_FILTERS
      .map(filter => ({
        key: filter.key,
        label: filter.label,
        tone: tones[filter.key] || 'good',
        count: previewData.filter(row => filter.statuses.includes(String(row.status || '').toLowerCase())).length,
      }))
      .filter(status => status.count > 0);
  }, [previewData]);

  return (
    <div className="admin-export" style={{ color: COLORS.textSecondary }}>
      <h2 className="section-title">📊 Export Reports</h2>
      <p style={{ marginBottom: '1.5rem', opacity: 0.7 }}>
        Generate booking reports by selecting a date range.
      </p>

      {/* Date Range Picker */}
      <div
        className="export-controls"
        style={{
          background: COLORS.cardSolid,
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '1.5rem',
          border: `1px solid ${COLORS.line}`,
        }}
      >
        <h3 style={{ color: COLORS.ink, marginTop: 0, marginBottom: '1rem' }}>Select Date Range</h3>
        <div
          className="date-inputs"
          style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}
        >
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ padding: '0.75rem', fontSize: '1rem' }}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ padding: '0.75rem', fontSize: '1rem' }}
            />
          </div>
          <button
            onClick={fetchBookings}
            disabled={loading}
            style={{
              padding: '0.75rem 1.5rem',
              background: COLORS.neonLime,
              color: COLORS.deepBg,
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? 'Loading...' : 'Fetch Bookings'}
          </button>

          {/* Status filter. Sits beside Fetch because it answers the same
              question — what goes in the report — but it never re-reads
              Firebase: the fetch pulls every status in the range and these
              boxes narrow what is shown and exported. */}
          <div className="form-group export-status-filter" style={{ marginBottom: 0 }}>
            <label>Show</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 1rem', paddingTop: '0.35rem' }}>
              {/* ALL is a master toggle, not a status of its own. */}
              <label style={statusBoxStyle}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => toggleAll(e.target.checked)}
                  style={{ margin: 0, cursor: 'pointer' }}
                />
                ALL
              </label>
              {STATUS_FILTERS.map(filter => (
                <label key={filter.key} style={statusBoxStyle}>
                  <input
                    type="checkbox"
                    checked={statusKeys.includes(filter.key)}
                    onChange={() => toggleStatus(filter.key)}
                    style={{ margin: 0, cursor: 'pointer' }}
                  />
                  {filter.label}
                </label>
              ))}
            </div>
          </div>
        </div>

        {statusKeys.length === 0 && (
          <p style={{ margin: '0.75rem 0 0', fontSize: '0.85rem', color: '#b3730a' }}>
            No status selected — tick at least one to see bookings.
          </p>
        )}
      </div>

      {/* Summary — a two-ring donut in place of the stat cards: where the
          money came from (inner) and what makes each source up (outer).
          Follows the status filter, like the preview and the file do. */}
      {!loading && !timedOut && previewData.length > 0 && (
        <ExportSummaryChart
          groups={chartGroups}
          recordCount={previewData.length}
          statusCounts={chartStatusCounts}
        />
      )}

      {/* Data Table — one tab per workbook sheet, showing that sheet's own
          columns exactly as they are written to the file. */}
      {!loading && !timedOut && previewData.length > 0 && (
        <div
          className="export-preview"
          style={{
            background: COLORS.cardSolid,
            borderRadius: '12px',
            overflow: 'hidden',
            border: `1px solid ${COLORS.line}`,
            marginBottom: '1.5rem',
          }}
        >
          <div
            style={{
              padding: '1rem 1.5rem',
              borderBottom: `1px solid ${COLORS.line}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '0.75rem',
            }}
          >
            <h3 style={{ color: COLORS.ink, margin: 0 }}>
              Preview ({previewData.length}
              {previewData.length !== fetchedCount ? ` of ${fetchedCount}` : ''} records)
            </h3>
            {/* One tab per sheet. The count is on the tab because the three
                sheets rarely fill up evenly, and an empty tab is worth
                seeing before the file is opened. */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {sheets.map((sheet, index) => {
                const count = sheetBodyRows(sheet).length;
                const active = index === activeSheet;
                return (
                  <button
                    key={sheet.name}
                    onClick={() => setActiveSheet(index)}
                    style={{
                      padding: '0.4rem 0.85rem',
                      borderRadius: '999px',
                      border: `1px solid ${active ? COLORS.neonLime : COLORS.line}`,
                      background: active ? COLORS.neonLime : 'transparent',
                      color: active ? COLORS.deepBg : COLORS.ink,
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      opacity: count === 0 && !active ? 0.55 : 1,
                    }}
                  >
                    {sheet.name} ({count})
                  </button>
                );
              })}
            </div>
          </div>

          {pageRows.length === 0 ? (
            <p style={{ padding: '2.5rem', textAlign: 'center', opacity: 0.6, margin: 0, fontSize: '0.9rem' }}>
              No {activeSheetName} records in this range.
            </p>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table
                  className="export-table"
                  style={{
                    borderCollapse: 'collapse',
                    tableLayout: 'fixed',
                    width: previewTableWidth ? `${previewTableWidth}px` : '100%',
                    minWidth: '100%',
                  }}
                >
                  {previewColumnPxWidths.length > 0 && (
                    <colgroup>
                      {previewColumnPxWidths.map((width, index) => (
                        <col key={index} style={{ width: `${width}px` }} />
                      ))}
                    </colgroup>
                  )}
                  <thead>
                    <tr style={{ background: '#f1efe9' }}>
                      {headerCells.map((cell, index) => (
                        <th
                          key={index}
                          style={{
                            padding: '0.75rem',
                            textAlign: 'left',
                            color: COLORS.ink,
                            fontSize: '0.78rem',
                            verticalAlign: 'bottom',
                          }}
                        >
                          {cell.value}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((row, rowIndex) => (
                      <tr
                        key={`${activeSheet}-${pageStart + rowIndex}`}
                        style={{
                          // Mirrors the bands the .xlsx puts on its rows —
                          // green for settled, amber for a reservation still
                          // owing — so the preview reads the same as the file
                          // it is previewing.
                          background: row.variant === 'confirmed'
                            ? 'rgba(217, 234, 211, 0.75)'
                            : row.variant === PAYMENT_STATUS_PARTIAL
                              ? 'rgba(252, 232, 200, 0.85)'
                              : (rowIndex % 2 === 0 ? 'transparent' : 'rgba(61, 18, 32, 0.03)'),
                          color: COLORS.ink,
                        }}
                      >
                        {row.cells.map((cell, cellIndex) => {
                          const numeric = cell?.type === 'money' || cell?.type === 'number';
                          return (
                            <td
                              key={cellIndex}
                              style={{
                                padding: '0.6rem 0.75rem',
                                fontSize: '0.8rem',
                                textAlign: numeric ? 'right' : 'left',
                                whiteSpace: numeric ? 'nowrap' : 'normal',
                                overflowWrap: 'anywhere',
                                verticalAlign: 'top',
                                color: cell?.type === 'money' ? COLORS.neonLime : COLORS.ink,
                              }}
                            >
                              {formatPreviewCell(cell, getCurrencySymbol(config))}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                  {/* The sheet's own totals row, repeated on every page so it
                      doesn't hide on the last one. */}
                  {footerRow && (
                    <tfoot>
                      <tr style={{ borderTop: `2px solid ${COLORS.line}`, fontWeight: 700 }}>
                        {footerRow.cells.map((cell, cellIndex) => {
                          const numeric = cell?.type === 'money' || cell?.type === 'number';
                          return (
                            <td
                              key={cellIndex}
                              style={{
                                padding: '0.7rem 0.75rem',
                                fontSize: '0.8rem',
                                textAlign: numeric ? 'right' : 'left',
                                whiteSpace: 'nowrap',
                                color: COLORS.ink,
                              }}
                            >
                              {formatPreviewCell(cell, getCurrencySymbol(config))}
                            </td>
                          );
                        })}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              <div
                style={{
                  padding: '0.75rem 1.5rem',
                  borderTop: `1px solid ${COLORS.line}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '0.75rem',
                }}
              >
                <span style={{ fontSize: '0.82rem', opacity: 0.7 }}>
                  Showing {pageStart + 1}&ndash;{pageStart + pageRows.length} of {bodyRows.length}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <button
                    onClick={() => setPage(safePage - 1)}
                    disabled={safePage === 0}
                    style={pagerButtonStyle(safePage === 0)}
                  >
                    Previous
                  </button>
                  <span style={{ fontSize: '0.82rem', color: COLORS.ink, fontWeight: 600 }}>
                    Page {safePage + 1} of {pageCount}
                  </span>
                  <button
                    onClick={() => setPage(safePage + 1)}
                    disabled={safePage >= pageCount - 1}
                    style={pagerButtonStyle(safePage >= pageCount - 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Ran past the configured timeout — the query is not coming back, so
          swap the spinner for an explanation and a retry. */}
      {timedOut && <ConnectionErrorScreen onRetry={handleRetry} />}

      {/* Loading state (CLAUDE.md > Smooth Display) */}
      {loading && !timedOut && (
        <div
          style={{
            background: COLORS.cardSolid,
            padding: '3rem',
            borderRadius: '12px',
            border: `1px solid ${COLORS.line}`,
            display: 'grid',
            placeItems: 'center',
            minHeight: '180px',
          }}
        >
          {spinnerRing('Loading bookings\u2026')}
        </div>
      )}

      {/* No Data Message */}
      {previewData.length === 0 && !loading && !timedOut && (
        <div
          style={{
            background: COLORS.cardSolid,
            padding: '3rem',
            borderRadius: '12px',
            textAlign: 'center',
            border: `1px solid ${COLORS.line}`,
          }}
        >
          <p style={{ opacity: 0.6, margin: 0 }}>
            {!fetched
              ? 'Select a date range and click Fetch Bookings to preview data.'
              : fetchedCount === 0
                ? 'No bookings found in the selected date range.'
                : `No bookings match the selected status${statusKeys.length === 1 ? '' : 'es'}. ${fetchedCount} booking${fetchedCount === 1 ? '' : 's'} in this range — tick another box to see ${fetchedCount === 1 ? 'it' : 'them'}.`}
          </p>
        </div>
      )}

      {/* Export Button */}
      {!loading && !timedOut && previewData.length > 0 && (
        <button
          onClick={generateWorkbook}
          style={{
            padding: '1rem 2rem',
            background: COLORS.neonLime,
            color: COLORS.deepBg,
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginTop: '1rem',
          }}
        >
          📥 Export as Excel (.xlsx)
        </button>
      )}
    </div>
  );
}

export default AdminExport;